import { useState, useRef, useMemo, useEffect } from "react";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtCurrency } from "@/lib/format";
import dayjs from "dayjs";
import { toast } from "sonner";
import {
  UploadCloud, FileText, Loader2, ShieldCheck, AlertTriangle, CheckCircle2,
  ShieldAlert, Building2, PackageCheck, PackagePlus, Eye, Plus, X, TrendingUp,
  TrendingDown, Minus, ScanText,
} from "lucide-react";

const num = (v) => (v === "" || v == null ? 0 : Number(v));
const MIME = { pdf: "application/pdf", jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png" };

function toBase64(file) {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1]); r.onerror = rej; r.readAsDataURL(file); });
}

function Chip({ tone, children, testId }) {
  const cls = {
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
    stone: "border-stone-200 bg-stone-100 text-stone-600",
  }[tone];
  return <span data-testid={testId} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>{children}</span>;
}

// Price-change banner comparing PO rate vs previous buyer price
function PriceDelta({ cmp }) {
  if (!cmp) return null;
  if (cmp.direction === "NEW") return <div className="text-[11px] text-stone-500">No previous buyer price — first record</div>;
  if (cmp.direction === "NONE") return null;
  if (cmp.direction === "SAME")
    return <div className="flex items-center gap-1 text-[11px] text-emerald-700"><Minus className="h-3 w-3" /> Buying price unchanged ({fmtCurrency(cmp.previous)})</div>;
  const up = cmp.direction === "UP";
  return (
    <div className={`flex flex-wrap items-center gap-x-2 text-[11px] ${up ? "text-amber-700" : "text-sky-700"}`}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      <strong>{up ? "Buying price changed" : "Buying price reduced"}</strong>
      <span>prev {fmtCurrency(cmp.previous)} → now {fmtCurrency(cmp.current)}</span>
      <span className="font-mono">({cmp.delta > 0 ? "+" : ""}{cmp.delta} · {cmp.deltaPct > 0 ? "+" : ""}{cmp.deltaPct}%)</span>
    </div>
  );
}

function CreateProductDialog({ open, onClose, initialName, onCreated }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ name: initialName || "", productType: "", category: "", subcategory: "", sku: "" }); }, [open, initialName]);

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Product name is required"); return; }
    if (!form.productType) { toast.error("Select Bakery or Non-Bakery"); return; }
    setBusy(true);
    try {
      const r = await api.post("/products", {
        name: form.name.trim(), productType: form.productType,
        category: form.category?.trim() || "", subcategory: form.subcategory?.trim() || "",
        sku: form.sku?.trim() || "", active: true, hasRecipe: false,
        classificationRequired: false, classificationSource: "USER", source: "BUYER_PO",
      });
      const p = r.data || {};
      onCreated({ id: p.id || p._id, productId: p.productId, name: p.name || form.name, sku: p.sku || form.sku, productType: form.productType });
      toast.success(`Product created (${p.productId || ""})`);
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Could not create product")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white sm:max-w-lg" data-testid="buyerpo-create-product">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><PackagePlus className="h-4 w-4 text-amber-700" /> Create New Product</DialogTitle>
          <DialogDescription className="text-xs">Only created when you explicitly choose to. Prefilled from the PO; classification is mandatory.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-1">
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs">Product Name</Label>
            <Input data-testid="buyerpo-np-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Classification <span className="text-rose-500">*</span></Label>
            <Select value={form.productType || ""} onValueChange={(v) => setForm({ ...form, productType: v })}>
              <SelectTrigger data-testid="buyerpo-np-classification" className={`bg-white ${!form.productType ? "border-amber-400" : ""}`}><SelectValue placeholder="Bakery / Non-Bakery" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="BAKERY">Bakery</SelectItem>
                <SelectItem value="NON_BAKERY">Non-Bakery</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">SKU (optional)</Label>
            <Input data-testid="buyerpo-np-sku" value={form.sku || ""} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Category (optional)</Label>
            <Input data-testid="buyerpo-np-category" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Sub-category (optional)</Label>
            <Input data-testid="buyerpo-np-subcategory" value={form.subcategory || ""} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="buyerpo-np-save" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={save}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create Product
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BuyerPoOrder() {
  const inputRef = useRef(null);
  const { data: buyerData, refetch: refetchBuyers } = useResource("/buyers");
  const { data: prodData, refetch: refetchProducts } = useResource("/products");
  const { data: companyData } = useResource("/settings/company");
  const buyers = Array.isArray(buyerData) ? buyerData : [];
  const products = Array.isArray(prodData) ? prodData : [];
  const tolerance = num(companyData?.poRoundingTolerance) || 0.01;

  const [file, setFile] = useState(null);
  const [working, setWorking] = useState(false);
  const [processed, setProcessed] = useState(null);
  const [lines, setLines] = useState([]);
  const [docTotals, setDocTotals] = useState(null);
  const [buyerId, setBuyerId] = useState("");
  const [buyerPoNumber, setBuyerPoNumber] = useState("");
  const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0, 10));
  const [deliveryDate, setDeliveryDate] = useState("");
  const [creating, setCreating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [createDlg, setCreateDlg] = useState(-1);

  const onExtract = async () => {
    if (!file) return;
    setWorking(true); setProcessed(null);
    const ext = (file.name || "").split(".").pop()?.toLowerCase() || "pdf";
    try {
      const b64 = await toBase64(file);
      const extRes = await api.post("/orders/import/pdf", { filename: file.name, contentBase64: b64, mimeType: MIME[ext] || "application/pdf" });
      const d = extRes.data || {};
      const head = d.order || d.header || d;
      setBuyerPoNumber(head.buyerPoNumber || "");
      setOrderDate(head.poDate || new Date().toISOString().slice(0, 10));
      setDeliveryDate(head.deliveryDate || "");
      const proc = await api.post("/orders/import/process", {
        buyerPoNumber: head.buyerPoNumber || "", poDate: head.poDate || "", deliveryDate: head.deliveryDate || "",
        buyer: d.buyer || {}, lines: d.lines || d.items || [], totals: d.totals || {},
      });
      const r = proc.data || {};
      setProcessed(r);
      setDocTotals(r.documentTotals || d.totals || null);
      refetchBuyers(); refetchProducts();
      setBuyerId(r.buyer?.status === "REVIEW" ? "" : (r.buyer?.id || ""));
      setLines((r.lines || []).map((l) => {
        const server = l.productStatus;
        const resolution = server === "MATCHED" ? "MATCHED" : server === "CREATED" ? "NEW_PRODUCT" : "REVIEW";
        return {
          extractedName: l.productName, quantity: l.quantity ?? "", rate: l.rate ?? "",
          discount: l.discount ?? 0, gstPct: l.gstPct ?? 0, gstDerivedFromTotal: !!l.gstDerivedFromTotal, unit: l.unit || "pcs",
          serverStatus: server, resolution,
          productId: (resolution === "REVIEW" ? "" : l.productId) || "",
          productCode: (resolution === "REVIEW" ? "" : l.productCode2 || l.productCode) || "",
          matchedName: (resolution === "REVIEW" ? "" : l.matchedProductName) || "",
          suggestedId: server === "REVIEW_MATCH" ? l.productId : "",
          suggestedName: server === "REVIEW_MATCH" ? l.matchedProductName : "",
          classificationRequired: !!l.classificationRequired,
          classificationSource: l.classificationSource || "",
          productType: l.productType || "",
          priceStatus: l.priceStatus, priceComparison: l.priceComparison || null,
        };
      }));
      toast.success("Extracted & processed — resolve any lines flagged for review");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Extraction failed"));
    } finally { setWorking(false); }
  };

  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const matchExisting = (i, id) => {
    const p = products.find((x) => x.id === id);
    setLine(i, {
      resolution: "MATCHED", productId: id, productCode: p?.productId || "",
      matchedName: p?.name || "", classificationRequired: !!p?.classificationRequired, productType: p?.productType || "",
    });
  };
  const acceptSuggestion = (i) => {
    const l = lines[i];
    const p = products.find((x) => x.id === l.suggestedId);
    setLine(i, { resolution: "MATCHED", productId: l.suggestedId, productCode: p?.productId || "", matchedName: l.suggestedName, classificationRequired: !!p?.classificationRequired });
  };
  const ignoreLine = (i) => setLine(i, { resolution: "IGNORED" });
  const unignore = (i) => setLine(i, { resolution: lines[i].productId ? "MATCHED" : "REVIEW" });
  const changeMatch = (i) => setLine(i, { resolution: "REVIEW", productId: "", productCode: "", matchedName: "" });

  const onProductCreated = (i, p) => {
    refetchProducts();
    setLine(i, {
      resolution: "NEW_PRODUCT", productId: p.id, productCode: p.productId, matchedName: p.name,
      classificationRequired: false, productType: p.productType, classificationSource: "USER",
    });
  };

  const saveClassification = async (i, classification) => {
    const l = lines[i];
    try {
      await api.put(`/products/${l.productId}`, { productType: classification, classificationRequired: false });
      setLine(i, { classificationRequired: false, productType: classification, classificationSource: "USER" });
      refetchProducts();
      toast.success(`Classified as ${classification === "BAKERY" ? "Bakery" : "Non-Bakery"}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not save classification")); }
  };

  // ---- derived gating ----
  const active = lines.filter((l) => l.resolution !== "IGNORED");
  const bookable = active.filter((l) => l.resolution === "MATCHED" || l.resolution === "NEW_PRODUCT");
  const unresolvedCount = active.filter((l) => l.resolution === "REVIEW").length;
  const pendingClass = bookable.filter((l) => l.classificationRequired).length;
  const invalidCount = bookable.filter((l) => num(l.quantity) <= 0 || num(l.rate) < 0).length;

  const systemTotal = useMemo(() => {
    let sub = 0, disc = 0, tax = 0;
    bookable.forEach((l) => {
      const amt = num(l.quantity) * num(l.rate);
      const ld = amt * (num(l.discount) / 100);
      const taxable = amt - ld;
      sub += amt; disc += ld; tax += taxable * (num(l.gstPct) / 100);
    });
    const freight = num(docTotals?.freight);
    return { subtotal: sub, discount: disc, tax, freight, grandTotal: sub - disc + tax + freight };
  }, [bookable, docTotals]);

  const docGrand = num(docTotals?.grandTotal);
  const hasDocTotal = docGrand > 0;
  const valueDiff = hasDocTotal ? Math.round((docGrand - systemTotal.grandTotal) * 100) / 100 : 0;
  const valueMatched = !hasDocTotal || Math.abs(valueDiff) <= tolerance;
  const isRounding = hasDocTotal && !!valueDiff && Math.abs(valueDiff) <= tolerance;

  const buyerReview = processed?.buyer?.status === "REVIEW";
  const dateWarning = orderDate && deliveryDate && dayjs(deliveryDate).isValid() && dayjs(orderDate).isValid()
    && dayjs(deliveryDate).isBefore(orderDate, "day");

  const canBook = !creating && !dateWarning && !!buyerId && bookable.length > 0
    && unresolvedCount === 0 && pendingClass === 0 && invalidCount === 0 && valueMatched;

  const reset = () => { setProcessed(null); setFile(null); setLines([]); setBuyerId(""); setBuyerPoNumber(""); setDocTotals(null); };

  const createDraft = async () => {
    setConfirmOpen(false); setCreating(true);
    const b = buyers.find((x) => x.id === buyerId);
    const payload = {
      buyerId, buyerName: b?.name || processed?.buyer?.name || "", buyerPoNumber, source: "BUYER_PO",
      orderDate, deliveryDate, status: "DRAFT",
      documentTotals: docTotals || undefined,
      lines: active.map((l) => {
        const p = products.find((x) => x.id === l.productId);
        return {
          resolution: l.resolution,
          sku: p?.sku || "", productName: p?.name || l.matchedName || l.extractedName,
          productCode: l.productCode, matched: l.resolution !== "REVIEW",
          quantity: num(l.quantity), unit: l.unit, rate: num(l.rate),
          discount: num(l.discount), gstPct: num(l.gstPct),
          capturePrice: true, amount: num(l.quantity) * num(l.rate),
        };
      }),
    };
    try {
      const res = await api.post("/orders", payload);
      toast.success(res.data?.idempotentReplay
        ? `This PO was already imported — reopened draft order ${res.data?.orderNumber || res.data?.orderNo || ""}`
        : `Draft order ${res.data?.orderNumber || res.data?.orderNo || ""} created from Buyer PO`);
      reset();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      toast.error(apiErrorMessage(e, code === "PO_VALUE_MISMATCH" ? "PO value mismatch — resolve before booking" : "Could not create draft order"));
    } finally { setCreating(false); }
  };

  const buyerChip = useMemo(() => {
    const bs = processed?.buyer?.status;
    if (bs === "EXISTING") return <Chip tone="green" testId="buyerpo-buyer-status">✓ Existing Buyer Matched{processed.buyer.matchedVia ? ` (${processed.buyer.matchedVia})` : ""}</Chip>;
    if (bs === "CREATED") return <Chip tone="amber" testId="buyerpo-buyer-status"><Plus className="h-3 w-3" /> New Buyer Created</Chip>;
    if (bs === "REVIEW") return <Chip tone="rose" testId="buyerpo-buyer-status">⚠ Buyer needs review</Chip>;
    return null;
  }, [processed]);

  const RES_CHIP = {
    MATCHED: <Chip tone="green">✓ Matched</Chip>,
    NEW_PRODUCT: <Chip tone="amber"><PackagePlus className="h-3 w-3" /> New Product</Chip>,
    REVIEW: <Chip tone="amber"><Eye className="h-3 w-3" /> Review Match</Chip>,
    IGNORED: <Chip tone="stone">Ignored</Chip>,
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Buyer PO Upload" subtitle="Upload a customer PO — resolve every line, verify the PO value, then create a draft order. No inventory is created." />

      <div className="flex items-start gap-3 rounded-lg border border-sky-200 bg-sky-50/70 px-4 py-3 text-sm text-sky-800">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
        <span>A Customer PO is <strong>not</strong> a sales invoice and never touches inventory. PO rates are captured as <strong>Buyer Prices</strong> (agreed selling prices). Every line must be resolved and the PO value must reconcile before booking.</span>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
        <div data-testid="buyerpo-dropzone" onClick={() => inputRef.current?.click()}
          className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-stone-300 bg-stone-50/50 py-10 text-center hover:border-amber-400 hover:bg-amber-50/40">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700"><ScanText className="h-6 w-6" /></div>
          {file ? <div className="flex items-center gap-2 text-sm font-medium text-stone-800"><FileText className="h-4 w-4 text-amber-700" /> {file.name}</div>
            : <><div className="font-display text-base font-semibold text-stone-800">Select a Buyer PO</div><div className="mt-1 text-xs text-muted-foreground">PDF · JPG · PNG</div></>}
          <input ref={inputRef} data-testid="buyerpo-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </div>
        <div className="mt-4 flex justify-end">
          <Button data-testid="buyerpo-extract" className="gap-2 bg-amber-700 hover:bg-amber-800" disabled={!file || working} onClick={onExtract}>
            {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Extract & Process
          </Button>
        </div>
      </div>

      {processed && (
        <div className="space-y-4 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="buyerpo-review">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-base font-semibold text-stone-900">PO Review</h3>
            {processed.idempotentReplay && <Chip tone="stone" testId="buyerpo-replay">✓ Existing PO detected — showing existing result</Chip>}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-stone-200 bg-stone-50/70 px-4 py-3" data-testid="buyerpo-buyer-banner">
            <div className="flex items-center gap-2.5">
              <Building2 className="h-4 w-4 text-stone-400" />
              <div>
                <div className="font-display text-sm font-bold uppercase text-stone-900">{processed.buyer?.name || processed.buyer?.extractedName || "Unknown buyer"}</div>
                {processed.buyer?.buyerId && <div className="font-mono text-xs text-stone-500">{processed.buyer.buyerId}</div>}
              </div>
            </div>
            {buyerChip}
          </div>

          {buyerReview && (
            <div className="space-y-1.5">
              <Label className="text-xs">Select the correct buyer (no automatic match could be made)</Label>
              <Select value={buyerId} onValueChange={setBuyerId}>
                <SelectTrigger data-testid="buyerpo-buyer-manual" className="h-9 max-w-md text-sm"><SelectValue placeholder="Match buyer manually" /></SelectTrigger>
                <SelectContent className="max-h-60 bg-white">{buyers.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} · {b.buyerId}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5"><Label>Buyer PO / Ref</Label><Input data-testid="buyerpo-ref" value={buyerPoNumber} onChange={(e) => setBuyerPoNumber(e.target.value)} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label>PO / Order Date</Label><Input data-testid="buyerpo-orderdate" type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label>Requested Delivery</Label><Input data-testid="buyerpo-delivery" type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="h-9 text-sm" /></div>
          </div>

          {dateWarning && (
            <div data-testid="buyerpo-date-warning" className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span>Delivery date is <strong>before</strong> the order date — verify the extracted dates.</span>
            </div>
          )}

          {/* Lines */}
          <div className="space-y-3">
            {lines.map((l, i) => {
              const ignored = l.resolution === "IGNORED";
              const needsClass = (l.resolution === "NEW_PRODUCT" || l.resolution === "MATCHED") && l.classificationRequired;
              return (
                <div key={i} data-testid={`buyerpo-line-${i}`}
                  className={`rounded-lg border p-3 ${ignored ? "border-stone-200 bg-stone-50/60 opacity-70" : l.resolution === "REVIEW" ? "border-amber-300 bg-amber-50/40" : "border-stone-200 bg-white"}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-[180px] flex-1">
                      <div className="font-medium text-stone-800">{l.extractedName}</div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-stone-500">
                        <span>Qty</span>
                        <Input data-testid={`buyerpo-qty-${i}`} type="number" min="0" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} className="h-7 w-20 text-right" disabled={ignored} />
                        <span>PO Rate</span>
                        <Input data-testid={`buyerpo-rate-${i}`} type="number" min="0" value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value })} className="h-7 w-24 text-right" disabled={ignored} />
                        <span>GST %</span>
                        <Input data-testid={`buyerpo-gst-${i}`} type="number" min="0" value={l.gstPct ?? ""} onChange={(e) => setLine(i, { gstPct: e.target.value })} className="h-7 w-16 text-right" disabled={ignored} />
                        <span className="font-mono text-stone-700">= {fmtCurrency(num(l.quantity) * num(l.rate))}</span>
                        {l.gstDerivedFromTotal && <span className="text-[10px] text-amber-700" title="GST derived from the document total">(tax from bill)</span>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5" data-testid={`buyerpo-line-status-${i}`}>
                      {RES_CHIP[l.resolution]}
                      {l.resolution !== "REVIEW" && l.classificationSource === "NAME_HEURISTIC" && !l.classificationRequired && <Chip tone="green">✓ Auto-classified</Chip>}
                    </div>
                  </div>

                  {/* Matched product / resolution actions */}
                  {!ignored && (l.resolution === "MATCHED" || l.resolution === "NEW_PRODUCT") && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-stone-100 pt-2">
                      <span className="text-xs text-stone-500">Product:</span>
                      <span className="text-sm font-medium text-stone-800">{l.matchedName}</span>
                      <span className="font-mono text-[11px] text-stone-400">{l.productCode}</span>
                      <Button size="sm" variant="ghost" className="h-6 gap-1 px-2 text-[11px]" data-testid={`buyerpo-change-${i}`} onClick={() => changeMatch(i)}>Change</Button>
                      <Button size="sm" variant="ghost" className="ml-auto h-6 gap-1 px-2 text-[11px] text-stone-500" data-testid={`buyerpo-ignore-${i}`} onClick={() => ignoreLine(i)}><X className="h-3 w-3" /> Ignore</Button>
                    </div>
                  )}

                  {!ignored && l.resolution === "REVIEW" && (
                    <div className="mt-2 space-y-2 border-t border-amber-200 pt-2">
                      <div className="text-xs font-semibold text-amber-800">⚠ Resolve this line — choose one:</div>
                      {l.suggestedId && (
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-stone-600">Suggested match: <strong>{l.suggestedName}</strong></span>
                          <Button size="sm" variant="outline" className="h-7 text-[11px]" data-testid={`buyerpo-accept-${i}`} onClick={() => acceptSuggestion(i)}>Accept suggestion</Button>
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <Select value={l.productId} onValueChange={(v) => matchExisting(i, v)}>
                          <SelectTrigger data-testid={`buyerpo-prod-${i}`} className="h-8 max-w-xs text-sm"><SelectValue placeholder="Match existing product…" /></SelectTrigger>
                          <SelectContent className="max-h-60 bg-white">{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.productId}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button size="sm" variant="outline" className="h-8 gap-1 text-[11px]" data-testid={`buyerpo-createnew-${i}`} onClick={() => setCreateDlg(i)}><PackagePlus className="h-3 w-3" /> Create New Product</Button>
                        <Button size="sm" variant="ghost" className="h-8 gap-1 px-2 text-[11px] text-stone-500" data-testid={`buyerpo-ignore-${i}`} onClick={() => ignoreLine(i)}><X className="h-3 w-3" /> Ignore line</Button>
                      </div>
                    </div>
                  )}

                  {ignored && (
                    <div className="mt-2 border-t border-stone-100 pt-2 text-right">
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" data-testid={`buyerpo-restore-${i}`} onClick={() => unignore(i)}>Restore line</Button>
                    </div>
                  )}

                  {/* classification required */}
                  {!ignored && needsClass && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5" data-testid={`buyerpo-class-editor-${i}`}>
                      <span className="text-[11px] font-semibold text-amber-800">⚠ Classification required:</span>
                      <Select value={l.productType || ""} onValueChange={(v) => saveClassification(i, v)}>
                        <SelectTrigger data-testid={`buyerpo-classification-${i}`} className="h-7 w-40 border-amber-400 bg-white text-xs"><SelectValue placeholder="Bakery / Non-Bakery" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="BAKERY" data-testid={`buyerpo-classification-bakery-${i}`}>Bakery</SelectItem>
                          <SelectItem value="NON_BAKERY" data-testid={`buyerpo-classification-nonbakery-${i}`}>Non-Bakery</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* buying price / history comparison */}
                  {!ignored && (l.resolution === "MATCHED" || l.resolution === "NEW_PRODUCT") && (
                    <div className="mt-2" data-testid={`buyerpo-line-price-${i}`}>
                      {l.priceStatus === "MISSING"
                        ? <div className="text-[11px] text-rose-600">⚠ Buying price not captured</div>
                        : <PriceDelta cmp={l.priceComparison} />}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* PO VALUE CHECK */}
          {hasDocTotal && (
            <div data-testid="buyerpo-value-check" className={`rounded-lg border p-4 ${valueMatched ? "border-emerald-200 bg-emerald-50/60" : "border-rose-300 bg-rose-50"}`}>
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-stone-800"><ShieldCheck className="h-4 w-4" /> PO Value Check</div>
              <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-xs sm:grid-cols-4">
                <div><div className="text-stone-500">Uploaded PO Total</div><div className="font-mono font-semibold" data-testid="buyerpo-doc-total">{fmtCurrency(docGrand)}</div></div>
                <div><div className="text-stone-500">System Subtotal</div><div className="font-mono">{fmtCurrency(systemTotal.subtotal)}</div></div>
                <div><div className="text-stone-500">Discount / Tax / Freight</div><div className="font-mono">{fmtCurrency(systemTotal.discount)} / {fmtCurrency(systemTotal.tax)} / {fmtCurrency(systemTotal.freight)}</div></div>
                <div><div className="text-stone-500">System Total</div><div className="font-mono font-semibold" data-testid="buyerpo-sys-total">{fmtCurrency(systemTotal.grandTotal)}</div></div>
              </div>
              <div className="mt-2 border-t border-stone-200 pt-2 text-sm">
                {valueMatched
                  ? <span className="font-semibold text-emerald-700" data-testid="buyerpo-value-ok">{isRounding ? `✓ Matched — rounding difference (₹${Math.abs(valueDiff)} ≤ tolerance ₹${tolerance})` : "✓ PO value matched"}</span>
                  : <span className="font-semibold text-rose-700" data-testid="buyerpo-value-mismatch">⚠ PO value mismatch — difference {fmtCurrency(Math.abs(valueDiff))} (tolerance ₹{tolerance}). Check qty, rate, missing/duplicate line, discount, tax, freight or an OCR error before booking.</span>}
              </div>
            </div>
          )}

          {/* gating notes */}
          {unresolvedCount > 0 && (
            <div data-testid="buyerpo-review-note" className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle className="h-4 w-4" /> {unresolvedCount} line(s) still need resolution (Match existing / Create new / Ignore).
            </div>
          )}
          {pendingClass > 0 && (
            <div data-testid="buyerpo-class-pending-note" className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle className="h-4 w-4" /> Classification pending for {pendingClass} product(s) — select Bakery / Non-Bakery.
            </div>
          )}

          <div className="flex items-center justify-between border-t border-stone-200 pt-3">
            <div className="text-sm"><span className="text-stone-500">Draft subtotal:&nbsp;</span><span data-testid="buyerpo-subtotal" className="font-mono font-semibold">{fmtCurrency(systemTotal.subtotal)}</span></div>
            <Button data-testid="buyerpo-create-order" className="gap-2 bg-emerald-600 hover:bg-emerald-700" disabled={!canBook} onClick={() => setConfirmOpen(true)}>
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Create Draft Order
            </Button>
          </div>
        </div>
      )}

      <CreateProductDialog open={createDlg >= 0} onClose={() => setCreateDlg(-1)}
        initialName={createDlg >= 0 ? lines[createDlg]?.extractedName : ""}
        onCreated={(p) => onProductCreated(createDlg, p)} />

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600" /> Create draft sales order</AlertDialogTitle>
            <AlertDialogDescription>Creates a DRAFT order ({bookable.length} line{bookable.length === 1 ? "" : "s"}). No invoice, dispatch, or stock movement. PO rates are captured as buyer prices with history preserved; commercial values are snapshotted. Uploading the same PO again reuses this order.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="buyerpo-confirm" className="bg-emerald-600 hover:bg-emerald-700" onClick={createDraft}>Yes, create draft</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
